import { AddressSearchInput } from '@/components/global/address/AddressSearchInput';
import { GoogleMapsProvider } from '@/components/global/address/GoogleMapsProviderSingleton';
import { Button } from '@/components/ui/shadcn/button';
import { useTranslations } from '@/hooks/useTranslations';
import { cn } from '@/lib/utils';
import { Check, Pencil, Trash2, X } from 'lucide-react';
import { useEffect, useState } from 'react';

interface EditableAddressFieldProps {
    value: string;
    onSave: (
        value: string,
        addressData?: {
            lat?: number;
            lng?: number;
            place_id?: string;
            street?: string;
            house_number?: string;
            city?: string;
            postal_code?: string;
        },
    ) => void;
    label?: string;
    placeholder?: string;
    className?: string;
    readOnly?: boolean;
}

export function EditableAddressField({
    value,
    onSave,
    label,
    placeholder = 'Enter address...',
    className,
    readOnly = false,
}: EditableAddressFieldProps) {
    const { t } = useTranslations();
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(value);
    const [addressData, setAddressData] = useState<
        | {
              lat?: number;
              lng?: number;
              place_id?: string;
              street?: string;
              house_number?: string;
              city?: string;
              postal_code?: string;
          }
        | undefined
    >();

    useEffect(() => {
        if (!isEditing) {
            setEditValue(value);
        }
    }, [isEditing, value]);

    const handleSave = () => {
        onSave(editValue, addressData);
        setIsEditing(false);
    };

    const handleCancel = () => {
        setEditValue(value);
        setAddressData(undefined);
        setIsEditing(false);
    };

    if (isEditing && !readOnly) {
        return (
            <GoogleMapsProvider>
                <div className={cn('space-y-2', className)}>
                    {label && <label className="text-sm font-medium">{label}</label>}
                    <div className="flex gap-2">
                        <div className="flex-1">
                            <AddressSearchInput
                                value={editValue}
                                onChange={setEditValue}
                                onAddressSelect={(address) => {
                                    setEditValue(address.label);
                                    setAddressData({
                                        lat: address.lat,
                                        lng: address.lng,
                                        place_id: address.id,
                                        street: address.street,
                                        house_number: address.house_number,
                                        city: address.city,
                                        postal_code: address.postal_code,
                                    });
                                }}
                                placeholder={placeholder}
                            />
                        </div>
                        <Button
                            type="button"
                            size="icon"
                            variant="outline"
                            onClick={() => {
                                setEditValue('');
                                setAddressData(undefined);
                            }}
                            className="shrink-0"
                            title="Clear address"
                        >
                            <Trash2 className="h-4 w-4" />
                        </Button>
                        <Button type="button" size="icon" variant="outline" onClick={handleSave} className="shrink-0">
                            <Check className="h-4 w-4" />
                        </Button>
                        <Button type="button" size="icon" variant="outline" onClick={handleCancel} className="shrink-0">
                            <X className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </GoogleMapsProvider>
        );
    }

    return (
        <div className={cn('group relative', className)}>
            {label && <label className="text-muted-foreground mb-1 block text-sm font-medium">{label}</label>}
            <div className="flex items-center gap-2">
                <span className={cn('flex-1', !value && 'text-muted-foreground italic')}>
                    {value || placeholder || t('work_orders.show.sidebar.no_address_provided', 'Not set')}
                </span>
                {!readOnly && (
                    <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
                        onClick={() => {
                            setEditValue(value);
                            setAddressData(undefined);
                            setIsEditing(true);
                        }}
                    >
                        <Pencil className="h-3.5 w-3.5" />
                    </Button>
                )}
            </div>
        </div>
    );
}
